import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MessageCircleQuestion } from "lucide-react";
import { getSession, interact, askCompass, getWritingPath, writingPathReadiness, writingPathWrite, writingPathIntroDismiss, writingPathEntry, writingPathStrengthenBegin, writingPathReturnToStrengthen, dueLabel, isPastDue } from "@/lib/api";
import WritingPhaseIntroCard, { introTransitionFor, reopenTransitionFor } from "@/components/WritingPhaseIntroCard";
import EssayWorkspace from "@/components/EssayWorkspace";
import StrengthenFinishWorkspace from "@/components/StrengthenFinishWorkspace";
import StrengthenWritingEntry from "@/components/StrengthenWritingEntry";
import { RouteChoice, SwitchRouteLink, MoveOnWithThesis, DoorChoice } from "@/components/WritingPathEntry";
import { toast } from "sonner";
import { WritingPathSkeleton } from "@/components/WritingPathBigPicture";

// THE WRITING-PATH SHELL — the five-phase strip, the assignment, and ONE workspace: the essay with the TWS dialogue beside it, from the
// first screen (How would you like to begin? → the thesis inside the Introduction) through the complete draft. Strengthen keeps its own flow.
const paper = "min-h-screen bg-[#faf9f6] paper-grain";
const btn = "inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-6 py-3 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-[#a34635] disabled:opacity-40 transition-colors";

export function Orientation({ o, onWhy }) {
  if (!o) return null;
  return (
    <div data-testid="wp-orientation" className="bg-white/80 border border-stone-200 rounded-md px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
      {/* THE ONLY PROCESS NAVIGATION in a Writing-Path session: the five phases, always visible, the current one distinct. */}
      <ol data-testid="wp-orientation-steps" className="flex flex-wrap items-center gap-x-4 gap-y-1 flex-1">
        {o.steps.filter((s) => s.key !== "get_ready").map((s, i) => (
          <li key={s.key} data-testid={`wp-step-${s.key}`} className={`flex items-center gap-2 text-[12px] ${s.status === "current" ? "text-stone-900 font-medium" : s.status === "completed" ? "text-stone-500" : "text-stone-400"}`}>
            <span className={`h-5 w-5 rounded-full border text-[10px] flex items-center justify-center ${s.status === "current" ? "border-[#8C3A2A] text-[#8C3A2A]" : s.status === "completed" ? "border-stone-300 bg-stone-200" : "border-stone-200"}`}>{s.status === "completed" ? "✓" : i + 1}</span>
            {s.label}
          </li>
        ))}
      </ol>
      {onWhy && <button data-testid="wp-intro-reopen" onClick={onWhy} className="text-[12px] text-[#8C3A2A] hover:text-[#6B2C20] underline underline-offset-4">Why are we doing this?</button>}
    </div>
  );
}

function AskTWS({ sessionId, draft, onSession }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const text = q.trim();
    if (!text || busy) return;
    setBusy(true); setQ("");
    setLog((l) => [...l, { role: "you", text }]);
    try {
      let s = await askCompass(sessionId, text, draft || "");
      let ai = [...(s.turns || [])].reverse().find((t) => t.role === "ai" && t.kind === "dialogue");
      for (let i = 0; i < 40 && ai && (ai.status === "processing" || ai.status === "streaming"); i++) {   // the reply arrives asynchronously
        await new Promise((r) => setTimeout(r, 2000));
        s = await getSession(sessionId);
        ai = (s.turns || []).find((t) => t.id === ai.id) || ai;
      }
      setLog((l) => [...l, { role: "tws", text: ai?.content || "I couldn't answer just now. Please try again." }]);
      onSession?.(s);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setLog((l) => [...l, { role: "tws", text: typeof d === "string" ? d : "I couldn't answer just now. Please try again." }]);
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="ask-tws" className="fixed bottom-5 right-5 z-40 w-[min(92vw,380px)]">
      {open && (
        <div data-testid="ask-tws-panel" className="mb-2 bg-white border border-stone-300 rounded-md shadow-xl p-4 max-h-[60vh] flex flex-col">
          <p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-2">Ask TWS</p>
          <div className="flex-1 overflow-y-auto space-y-2 text-[13px] mb-3">
            {log.length === 0 && <p className="text-stone-500">Ask anything — "What am I doing now?", "Is this evidence or a main idea?", "Am I missing something?", "Can I change my thesis?", "What should I do next?"</p>}
            {log.map((m, i) => <p key={i} data-testid={`ask-tws-${m.role}`} className={m.role === "you" ? "text-stone-900" : "text-stone-700 bg-stone-50 border border-stone-200 rounded-sm p-2"}>{m.text}</p>)}
            {busy && <Loader2 className="h-4 w-4 animate-spin text-stone-400" />}
          </div>
          <div className="flex gap-2">
            <input data-testid="ask-tws-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder="Type your question" className="flex-1 border border-stone-300 rounded-sm px-3 py-2 text-sm" />
            <button data-testid="ask-tws-send" onClick={send} disabled={busy || !q.trim()} className={btn}>Ask</button>
          </div>
        </div>
      )}
      <button data-testid="ask-tws-button" onClick={() => setOpen(!open)} className="ml-auto flex items-center gap-2 bg-stone-900 text-stone-50 px-4 py-3 rounded-full shadow-lg hover:bg-stone-800 transition-colors font-mono-panel text-xs uppercase tracking-[0.18em]">
        <MessageCircleQuestion className="h-4 w-4" /> Ask TWS
      </button>
    </div>
  );
}

export default function ThesisFirstShell({ session, onSession }) {
  const [wp, setWp] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [wholeOpens, setWholeOpens] = useState(0);   // bumped when TWS itself opens the whole essay (after a whole-draft check)
  const [bigOpen, setBigOpen] = useState(false);     // Big Picture — the only alternate view — replaces the essay until "Back to your essay"
  useEffect(() => { if (bigOpen) window.scrollTo({ top: 0, behavior: "smooth" }); }, [bigOpen]);
  const pollRef = useRef(null);
  const sid = session?.id;
  const seqRef = useRef(0);   // a refresh that started before a later action response must never overwrite it
  const refreshWp = useCallback(async () => { if (!sid) return; const my = ++seqRef.current; const data = await getWritingPath(sid); if (my === seqRef.current) setWp(data); }, [sid]);
  const applyUpdate = (fn) => { seqRef.current++; setWp(fn); };
  useEffect(() => { refreshWp().catch(() => {}); }, [refreshWp]);

  const processing = !!session?.turns?.some((t) => t.status === "processing" || t.status === "streaming");
  useEffect(() => {
    if (!processing || !sid) return undefined;
    pollRef.current = setInterval(async () => {
      try { onSession(await getSession(sid)); } catch (_) { /* ignore */ }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [processing, sid, onSession]);
  // The phase is re-read on the processing → complete edge, whoever observed completion first (this shell's poll or
  // the studio's own); refreshing inside the poll callback lost the race and left the student on the main-point surface.
  const wasProcessing = useRef(false);
  useEffect(() => {
    if (wasProcessing.current && !processing) refreshWp().catch(() => {});
    wasProcessing.current = processing;
  }, [processing, refreshWp]);

  const phase = wp?.phase || session?.writing_path_phase || "get_ready";
  const entry = wp?.entry;
  const strengthenMode = wp?.revise?.entry_mode === "strengthen_existing" || (!wp?.revise && session?.entry_mode === "strengthen_existing" && phase === "strengthen_finish");
  const strengthenPending = session?.entry_mode === "strengthen_existing" && session?.strengthen_pending && phase !== "strengthen_finish";
  const noAssignment = !session?.assignment || /^Independent writing — no assignment entered\.$/.test(session.assignment);
  const [switching, setSwitching] = useState(false);
  const applyEntry = (r) => applyUpdate((prev) => ({ ...(prev || {}), entry: r.entry, orientation: r.orientation, phase: r.phase, working_thesis: r.working_thesis }));
  const chooseRoute = async (route) => { setBusy(true); try { applyEntry(await writingPathEntry(sid, { action: "choose", route })); setSwitching(false); await refreshWp(); } finally { setBusy(false); } };
  const entryStage = entry?.route === "ideas_first" ? (entry.ideas_stage || "explore") : entry?.route === "draft_first" ? (entry.draft_stage || "enter") : "thesis";
  const needsChoice = !!entry?.needs_choice;
  const lastAi = [...(session?.turns || [])].reverse().find((t) => t.role === "ai" && t.status !== "processing");
  const o = wp?.orientation;

  // No separate Get-ready screen: a session whose way to begin is already set goes straight to the essay.
  const needsDoor = !!entry?.needs_door_choice;
  useEffect(() => {
    if (!wp || phase !== "get_ready" || needsChoice || needsDoor || session?.strengthen_pending || session?.entry_mode === "strengthen_existing") return;
    writingPathReadiness(sid, "ready", "").then(() => refreshWp()).catch(() => {});
  }, [wp, phase, needsChoice, needsDoor, session?.strengthen_pending, session?.entry_mode, sid, refreshWp]);

  // GUIDED PHASE INTRODUCTION — derived from the step the student is on; shown once per session per step (flags live on the session).
  const organizeStarted = false;   // the essay is live from the first thesis: there is no separate Organize step (its card is not shown)
  const autoIntro = wp ? introTransitionFor({ phase, route: entry?.route || wp?.writing_path, entryStage, needsChoice: needsChoice || switching, organizeStarted }) : "";
  const reopenIntro = wp ? reopenTransitionFor(autoIntro, { route: entry?.route || wp?.writing_path, phase }) : "";
  const introDismissed = wp?.intro_dismissed || [];
  const [introOpen, setIntroOpen] = useState("");
  const seen = (t) => introDismissed.includes(t) || (t === "develop_essay_start" && ["thesis_recognized", "organize_start", "write_start"].some((x) => introDismissed.includes(x)));
  useEffect(() => { if (autoIntro && !seen(autoIntro)) setIntroOpen(autoIntro); }, [autoIntro]);   // eslint-disable-line react-hooks/exhaustive-deps
  const dismissIntro = useCallback(() => {
    const t = introOpen;
    setIntroOpen("");
    if (!t || introDismissed.includes(t) || t === "build_argument_explained") return;
    setWp((prev) => (prev ? { ...prev, intro_dismissed: [...(prev.intro_dismissed || []), t] } : prev));
    writingPathIntroDismiss(sid, t).catch(() => {});
  }, [introOpen, introDismissed, sid]);

  const shareThesis = async (text) => {
    if (!text || busy || processing) return;
    setBusy(true);
    try { const s = await interact(sid, { content: text, kind: "writing" }); onSession(s); await refreshWp(); } finally { setBusy(false); }
  };

  const mode = phase === "write_essay" || phase === "build_argument" ? "develop" : "thesis";   // ONE phase, Develop the essay, for both stored values
  const showEssay = !needsChoice && !switching && (phase === "develop_main_point" || phase === "build_argument" || (phase === "write_essay" && wp?.write));
  const wide = phase === "develop_main_point" || phase === "build_argument" || phase === "write_essay" || phase === "strengthen_finish";
  const onWorkspaceUpdate = (r) => {
    if (r.kind === "entry") { applyEntry(r); return; }
    applyUpdate((prev) => ({ ...(prev || {}), ...(r.kind === "argument"
      ? { working_thesis: r.working_thesis, orientation: r.orientation, argument: r, organize: r.organize, write: r.write || prev?.write }
      : r.kind === "organize"
        ? { working_thesis: r.working_thesis, orientation: r.orientation, argument: r.argument, organize: r.organize, phase: r.phase, write: r.write || prev?.write, revise: r.revise || prev?.revise }
        : { working_thesis: r.working_thesis, orientation: r.orientation, argument: r.argument, organize: r.organize, write: r.write, phase: r.phase }) }));
    if ((r.kind === "organize" && r.phase === "write_essay") || (r.kind === "write" && r.phase !== "write_essay")) refreshWp().catch(() => {});
  };

  if (needsDoor && entry) {   // "Let students choose": the one place a student picks between the two doors
    return (
      <div className={paper}>
        <div className="mx-auto w-full max-w-3xl px-6 py-8 space-y-6">
          <DoorChoice entry={entry} busy={busy} onChoose={async (door) => {
            setBusy(true);
            try { await writingPathEntry(sid, { action: "choose_door", route: door }); onSession(await getSession(sid)); await refreshWp(); }
            catch (e) { toast.error(e?.response?.data?.detail || "Could not set the way to work."); }
            finally { setBusy(false); }
          }} />
        </div>
      </div>
    );
  }

  if (strengthenPending) {
    return (
      <div className={paper} data-testid="wp-strengthen-pending">
        <div className="mx-auto w-full max-w-5xl px-6 pt-8"><Orientation o={o} onWhy={null} /></div>
        <StrengthenWritingEntry assignment={session?.assignment || ""} assignmentLocked assignmentTitle={wp?.assignment_title || ""} dueDate={wp?.due_date ? dueLabel(wp.due_date) : ""} onStart={async (_prompt, text) => {
          await writingPathStrengthenBegin(sid, text);
          onSession(await getSession(sid));
          await refreshWp();
          window.scrollTo({ top: 0, behavior: "smooth" });
        }} />
      </div>
    );
  }

  return (
    <div className={paper}>
      <div className={`mx-auto w-full px-6 py-8 space-y-6 ${wide ? "max-w-5xl" : "max-w-3xl"}`}>
        <Orientation o={o} onWhy={reopenIntro ? () => setIntroOpen(reopenIntro) : null} />
        {introOpen && <WritingPhaseIntroCard transition={introOpen} thesis={wp?.working_thesis || session?.working_thesis} onDismiss={dismissIntro} />}
        {!noAssignment && <section data-testid="wp-assignment" className="bg-white border border-stone-200 rounded-md px-5 py-3">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-stone-500">Assignment</p>
            {wp?.due_date ? <p data-testid="wp-due-date" className={`font-mono-panel text-[10px] uppercase tracking-[0.22em] ${isPastDue(wp.due_date) ? "text-[#8C3A2A]" : "text-stone-500"}`}>{dueLabel(wp.due_date)}{isPastDue(wp.due_date) ? " · past due" : ""}</p> : null}
          </div>
          <p className="text-[15px] text-stone-800 whitespace-pre-wrap leading-relaxed">{session?.assignment}</p>
          {wp?.assignment_context?.requires_assigned_material && ["get_ready", "develop_main_point"].includes(phase) && (
            <p data-testid="wp-assigned-material" className="mt-2 text-[13px] text-stone-700"><span className="font-mono-panel text-[10px] uppercase tracking-[0.2em] text-[#8C3A2A] mr-2">Work with first</span>{wp.assignment_context.assigned_material_note || "The material your teacher assigned"}</p>
          )}
          {!needsChoice && !switching && phase === "develop_main_point" && <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1"><SwitchRouteLink entry={entry} busy={busy} onSwitch={() => setSwitching(true)} /><MoveOnWithThesis sessionId={sid} entry={entry} busy={busy || processing} onMoved={async () => { onSession(await getSession(sid)); await refreshWp(); }} /></div>}
          {session?.escalated_from === "strengthen_existing" && phase !== "strengthen_finish" && (
            <div data-testid="wp-return-to-strengthen" className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-stone-100 pt-3">
              <p className="text-[12.5px] text-stone-500">You moved here from Strengthen. Nothing is lost if you go back — your current text comes with you.</p>
              <button data-testid="wp-return-to-strengthen-btn" disabled={busy || processing}
                onClick={async () => { try { await writingPathReturnToStrengthen(sid); onSession(await getSession(sid)); await refreshWp(); window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) { /* the shell shows the current state */ } }}
                className="text-[12.5px] text-[#8C3A2A] underline underline-offset-4 hover:text-[#6f2d21]">Go back to strengthening what I wrote</button>
            </div>
          )}
        </section>}

        {(needsChoice || switching) && !strengthenMode && ["get_ready", "develop_main_point"].includes(phase) && entry && (
          <RouteChoice entry={entry} busy={busy} onChoose={chooseRoute} switching={switching && !needsChoice} />
        )}
        {switching && !needsChoice && <button data-testid="wp-switch-cancel" onClick={() => setSwitching(false)} className="text-[12px] text-stone-500 underline underline-offset-4">Keep going the way I started</button>}

        {/* YOUR ESSAY — the one workspace: thesis → argument → organize → write, all on the same surface with TWS beside it. */}
        {showEssay && !bigOpen && (
          <EssayWorkspace sessionId={sid} onBigPicture={() => setBigOpen(true)} session={session} onSession={onSession} thesis={wp?.working_thesis || session?.working_thesis} mode={mode} phase={phase} entry={entry}
            organize={wp?.organize} argument={wp?.argument} write={wp?.write} processing={processing || busy} onShareThesis={shareThesis}
            lastCoach={phase === "develop_main_point" && lastAi?.content && lastAi.kind !== "dialogue" ? lastAi.content : ""}
            onDraftChange={setDraft} onWholeCheck={() => setWholeOpens((n) => n + 1)} onUpdate={onWorkspaceUpdate} onRefresh={() => refreshWp().catch(() => {})} />
        )}
        {phase === "strengthen_finish" && wp?.revise && (
          <StrengthenFinishWorkspace sessionId={sid} thesis={wp?.working_thesis || session?.working_thesis} revise={wp.revise} onDraftChange={setDraft} assignmentLinked={!!session?.assignment_id}
            onUpdate={(r) => applyUpdate((prev) => ({ ...(prev || {}), working_thesis: r.working_thesis, orientation: r.orientation, argument: r.argument, organize: r.organize, write: r.write, revise: r.revise, phase: r.phase }))}
            onEscalated={async () => { try { onSession(await getSession(sid)); } catch (_) { /* ignore */ } await refreshWp(); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        )}

        {(wp?.write?.parts || []).length > 0 && !strengthenMode && (
          <SecondaryViews sid={sid} thesis={wp?.working_thesis || session?.working_thesis} write={wp.write || { parts: wp.organize.parts.map((p) => ({ ...p, text: "", word_count: 0 })) }} revise={wp.revise} organize={wp.organize} argument={wp.argument} openWhole={wholeOpens} open={bigOpen} onOpen={() => setBigOpen(true)} onClose={() => setBigOpen(false)}
            onGoTo={async (partId) => {
              if (partId && phase === "write_essay") { const r = await writingPathWrite(sid, { action: "open_part", part_id: partId }); applyUpdate((prev) => ({ ...(prev || {}), orientation: r.orientation, write: r.write })); }
              setTimeout(() => document.querySelector(partId ? `[data-testid='essay-part-${partId}'], [data-testid='sf-whole-${partId}']` : "[data-testid='essay-workspace'], [data-testid='sf-main']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
            }} />
        )}
      </div>
      {!showEssay && <AskTWS sessionId={sid} draft={draft} onSession={onSession} />}
    </div>
  );
}

// SECONDARY VIEWS — "Show me the whole essay" and "View full structure", built from the state the student already has. Nothing the next
// action needs lives only here; the old Studio (its stage navigation, Tools, Scratch pad, Essay/Structure toggle) is not part of a Writing-Path session.
// When TWS opens the whole essay itself (after a whole-draft check), it says why first — a deliberate instructional move, never a screen change.
function SecondaryViews({ sid, thesis, write, revise, openWhole, open, onOpen, onClose, onGoTo }) {
  const [framing, setFraming] = useState("");          // "card" (first automatic opening) · "banner" (later ones) · "" (student opened it)
  const [overlayOpen, setOverlayOpen] = useState(false);
  const autoOpens = useRef(0);
  const parts = write?.parts || [];
  const needs = revise?.coach_message || (["gap", "drift", "thesis_changed", "share_issue", "share_big_picture"].includes(write?.coach_issue) ? write?.coach_message : "") || "";
  const curId = write?.current_part_id || "";
  const TAG = "font-mono-panel text-[10px] uppercase tracking-[0.2em]";
  useEffect(() => {
    if (!openWhole) return;
    autoOpens.current += 1;
    onOpen();
    setFraming(autoOpens.current === 1 ? "card" : "banner");
    setOverlayOpen(autoOpens.current === 1);
    setTimeout(() => document.querySelector("[data-testid='wp-whole-essay']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [openWhole]);
  return (
    <section data-testid="wp-secondary" className="mt-2">
      {open && (
        <article data-testid="wp-whole-essay" className="bg-white border border-stone-200 rounded-md p-6 space-y-5">
          <div className="flex items-baseline justify-between gap-3">
            <h1 className="font-serif-display text-2xl text-stone-900">Big Picture</h1>
            <button data-testid="wp-whole-back" onClick={() => { onClose(); onGoTo?.(curId); }} className="text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-4">← Back to your essay</button>
          </div>
          {framing === "card" && overlayOpen && (
            <div data-testid="wp-whole-overlay" className="border-2 border-[#8C3A2A] bg-[#f8f4ee] rounded-md p-6">
              <p className={`${TAG} text-[#8C3A2A]`}>TWS</p>
              <p className="font-serif-display text-2xl text-stone-900 mt-1">Let's step back and look at the whole essay.</p>
              <p className="text-[15px] text-stone-800 mt-3">I'm showing you Big Picture so you can see:</p>
              <ul className="text-[15px] text-stone-800 list-disc pl-6 mt-1 space-y-0.5">
                <li>your thesis — the organizing idea — and where it sits,</li><li>what each part of the essay is doing,</li><li>what still needs developing,</li><li>where to go next.</li>
              </ul>
              <p className="text-[15px] text-stone-800 mt-3">Look for <span className="text-[#8C3A2A] font-medium">→ YOU ARE HERE</span> and <span className="font-medium">NEXT</span>. When you're ready, return to the essay to keep writing.</p>
              <button data-testid="wp-whole-overlay-dismiss" onClick={() => setOverlayOpen(false)} className="mt-4 inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-5 py-2.5 rounded-sm text-[14px] hover:bg-[#6B2C20]">Show me</button>
            </div>
          )}
          {framing === "banner" && <p data-testid="wp-whole-banner" className="text-[13px] text-stone-800 bg-stone-50 border border-stone-200 rounded-sm p-3"><span className={`${TAG} text-[#8C3A2A] mr-2`}>TWS</span>Stepping back to the whole essay — look for <span className="text-[#8C3A2A]">→ You are here</span> and <span className="text-stone-600">Next</span>.</p>}
          {!(framing === "card" && overlayOpen) && (
            <>
              <p data-testid="wp-whole-orientation" className="text-[12px] text-stone-500">How this essay gets from its thesis to its conclusion — the structure and its connections, not the prose.</p>
              <WritingPathSkeleton sessionId={sid} parts={parts} thesis={thesis} curId={curId} coachNeeds={needs} onGoTo={(id) => { onClose(); onGoTo?.(id); }} />
            </>
          )}
        </article>
      )}
    </section>
  );
}
